import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from "./components/dashboard/layout";

const BusAttendanceReports = () => {
  const [fromDate, setFromDate] = useState(() => new Date());
  const [toDate, setToDate]     = useState(() => new Date());
  const [session, setSession]   = useState(''); // '', 'morning', 'evening'
  const [route, setRoute]       = useState('');
  const [search, setSearch]     = useState('');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [routeSummary, setRouteSummary] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, limit: 100, offset: 0 });

  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 Summary, 1 Details

  const formatDate = (d) => {
    if (!d) return '';
    const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,'0'); const day = String(d.getDate()).padStart(2,'0');
    return `${y}-${m}-${day}`;
  };

  const styles = {
    container: { padding: '2rem', maxWidth: '100%' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
    title: { fontSize: '1.8rem', fontWeight: 700, margin: 0 },
    inputsRow: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 },
    input: { padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6, minWidth: 160 },
    select: { padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6, minWidth: 160 },
    button: { padding: '10px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
    buttonSecondary: { padding: '10px 16px', background: '#455a64', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
    disabled: { background: '#ccc', cursor: 'not-allowed' },
    lastUpdated: { fontSize: 12, color: '#666', marginBottom: 8 },
    tabs: { display: 'flex', gap: 8, borderBottom: '2px solid #eee', marginTop: 8 },
    tab: { padding: '10px 16px', border: 'none', background: 'transparent', borderBottom: '2px solid transparent', cursor: 'pointer' },
    activeTab: { borderBottom: '2px solid #1976d2', color: '#1976d2', fontWeight: 600 },
    cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, margin: '12px 0' },
    card: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' },
    cardTitle: { fontSize: 12, color: '#666', marginBottom: 4, textTransform: 'uppercase' },
    cardValue: { fontSize: 22, fontWeight: 700 },
    tableWrap: { overflowX: 'auto', marginTop: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' },
    th: { padding: '12px 16px', borderBottom: '2px solid #ddd', fontWeight: 600, textAlign: 'left', background: '#f8f9fa' },
    td: { padding: '10px 16px', borderBottom: '1px solid #eee' },
    empty: { textAlign: 'center', padding: 20, color: '#777' },
    pager: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }
  };

  const fetchLogs = async (opts = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const limit  = opts.limit  ?? meta.limit  ?? 100;
      const offset = opts.offset ?? meta.offset ?? 0;
      const params = new URLSearchParams({
        from_date: formatDate(fromDate),
        to_date:   formatDate(toDate),
        session,
        route,
        search,
        limit: String(limit),
        offset: String(offset)
      });
      const url = `https://raptornet.aischennai.org/screening-manager/gate-api/getBusAttendanceLogs.php?${params.toString()}`;
      const res = await fetch(url);
      const json = await res.json();

      if (!json.success) throw new Error(json.error || 'API error');

      setRows(Array.isArray(json.data) ? json.data : []);
      setSummary(Array.isArray(json.summary) ? json.summary : []);
      setRouteSummary(Array.isArray(json.route_summary) ? json.route_summary : []);
      setMeta(json.meta || { total_count: 0, limit, offset });
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Fetch error:', e);
      setError(e.message || 'Failed to load data.');
      setRows([]); setSummary([]); setRouteSummary([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, []);

  const totalRows = meta?.total_count || 0;
  const presentTotal = useMemo(
    () => rows.filter(r => Number(r.is_present) === 1).length,
    [rows]
  );
  const idCardNo = useMemo(
    () => rows.filter(r => Number(r.has_id_card) === 0).length,
    [rows]
  );

  const toCSV = () => {
    const header = [
      'Log Date','Log Time','Route ID','Route Name','Chaperone ID','Chaperone Name',
      'Student ID','Student Name','Session','Present','Has ID Card','Created By','Created At'
    ];
    const lines = rows.map(r => ([
      r.log_date, r.log_time, r.route_id, r.route_name, r.chaperone_id, r.chaperone_name,
      r.student_id, r.student_name, r.session, r.is_present ? 'Yes' : 'No',
      r.has_id_card ? 'Yes' : 'No', '', r.created_at || ''
    ]));
    const csv = [header, ...lines]
      .map(cols => cols.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bus_attendance_${formatDate(fromDate)}_to_${formatDate(toDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const nextPage = () => {
    const newOffset = (meta.offset || 0) + (meta.limit || 100);
    if (newOffset < totalRows) fetchLogs({ offset: newOffset });
  };
  const prevPage = () => {
    const newOffset = Math.max(0, (meta.offset || 0) - (meta.limit || 100));
    fetchLogs({ offset: newOffset });
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Bus Attendance Logs</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...styles.button, ...(isLoading ? styles.disabled : {}) }}
              onClick={() => fetchLogs()}
              disabled={isLoading}
            >
              {isLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button style={styles.buttonSecondary} onClick={toCSV}>Export CSV</button>
          </div>
        </div>

        {lastUpdated && <div style={styles.lastUpdated}>Last updated: {lastUpdated.toLocaleString()}</div>}
        {error && <div style={{ marginBottom: 8, padding: '10px 12px', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: 8, color: '#b00020' }}>{error}</div>}

        {/* Filters */}
        <div style={styles.inputsRow}>
          <input type="date" value={formatDate(fromDate)} onChange={e => setFromDate(new Date(e.target.value))} style={styles.input} />
          <input type="date" value={formatDate(toDate)} onChange={e => setToDate(new Date(e.target.value))} style={styles.input} />
          <select value={session} onChange={e => setSession(e.target.value)} style={styles.select}>
            <option value="">All Sessions</option>
            <option value="morning">Morning</option>
            <option value="evening">Evening</option>
          </select>
          <input type="text" placeholder="Route (ID or Name)" value={route} onChange={e => setRoute(e.target.value)} style={styles.input} />
          <input type="text" placeholder="Search Student/Chaperone" value={search} onChange={e => setSearch(e.target.value)} style={{...styles.input, minWidth: 240}} />
          <button
            style={{ ...styles.button, ...(isLoading ? styles.disabled : {}) }}
            onClick={() => fetchLogs({ offset: 0 })}
            disabled={isLoading}
          >
            Apply
          </button>
        </div>

        {/* KPI Cards */}
        <div style={styles.cards}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Rows (page)</div>
            <div style={styles.cardValue}>{rows.length}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Rows (total)</div>
            <div style={styles.cardValue}>{totalRows}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Present (page)</div>
            <div style={styles.cardValue}>{presentTotal}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>No ID Card (page)</div>
            <div style={styles.cardValue}>{idCardNo}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button style={{...styles.tab, ...(activeTab === 0 ? styles.activeTab : {})}} onClick={() => setActiveTab(0)}>Summary</button>
          <button style={{...styles.tab, ...(activeTab === 1 ? styles.activeTab : {})}} onClick={() => setActiveTab(1)}>Details</button>
        </div>

        {/* Summary Tab */}
        {activeTab === 0 && (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Session</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Present</th>
                    <th style={styles.th}>Absent</th>
                    <th style={styles.th}>ID Card: Yes</th>
                    <th style={styles.th}>ID Card: No</th>
                    <th style={styles.th}>Present %</th>
                    <th style={styles.th}>ID Card %</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 ? (
                    <tr><td colSpan={8} style={styles.empty}>{isLoading ? 'Loading…' : 'No summary.'}</td></tr>
                  ) : summary.map((s, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{s.session}</td>
                      <td style={styles.td}>{s.total}</td>
                      <td style={styles.td}>{s.present}</td>
                      <td style={styles.td}>{s.absent}</td>
                      <td style={styles.td}>{s.idcard_yes}</td>
                      <td style={styles.td}>{s.idcard_no}</td>
                      <td style={styles.td}>{s.present_pct}%</td>
                      <td style={styles.td}>{s.idcard_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: 20 }}>By Route & Session</h3>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Route</th>
                    <th style={styles.th}>Session</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Present</th>
                    <th style={styles.th}>ID Card Yes</th>
                  </tr>
                </thead>
                <tbody>
                  {routeSummary.length === 0 ? (
                    <tr><td colSpan={5} style={styles.empty}>{isLoading ? 'Loading…' : 'No route summary.'}</td></tr>
                  ) : routeSummary.map((r, i) => (
                    <tr key={`${r.route_id}-${r.session}-${i}`}>
                      <td style={styles.td}>{r.route_name || r.route_id}</td>
                      <td style={styles.td}>{r.session}</td>
                      <td style={styles.td}>{r.total}</td>
                      <td style={styles.td}>{r.present}</td>
                      <td style={styles.td}>{r.idcard_yes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Details Tab */}
        {activeTab === 1 && (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Time</th>
                    <th style={styles.th}>Route</th>
                    <th style={styles.th}>Chaperone</th>
                    <th style={styles.th}>Student</th>
                    <th style={styles.th}>Session</th>
                    <th style={styles.th}>Present</th>
                    <th style={styles.th}>ID Card</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={9} style={styles.empty}>{isLoading ? 'Loading…' : 'No rows.'}</td></tr>
                  ) : rows.map((r) => (
                        <tr
                        key={r.id}
                        style={
                            Number(r.is_present) === 1 && Number(r.has_id_card) === 0
                            ? { backgroundColor: '#fff3cd' } // light yellow (warning)
                            : {}
                        }
                        >
                      <td style={styles.td}>{r.log_date}</td>
                      <td style={styles.td}>{r.log_time?.slice(11, 19) || r.log_time}</td>
                      <td style={styles.td}>{`${r.route_name || ''}`}</td>
                      <td style={styles.td}>{`${r.chaperone_name || ''}`}</td>
                      <td style={styles.td}>{`${r.student_name || ''} (${r.student_id})`}</td>
                      <td style={styles.td}>{r.session}</td>
                      <td style={styles.td}>{r.is_present ? '✅' : '❌'}</td>
                      <td style={styles.td}>{r.has_id_card ? '🪪 Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={styles.pager}>
              <button
                style={{ ...styles.button, ...(meta.offset <= 0 ? styles.disabled : {}) }}
                onClick={prevPage}
                disabled={meta.offset <= 0}
              >
                ◀ Prev
              </button>
              <span>Showing {meta.offset + 1}-{Math.min(meta.offset + (meta.limit || 100), totalRows)} of {totalRows}</span>
              <button
                style={{ ...styles.button, ...(((meta.offset + (meta.limit || 100)) >= totalRows) ? styles.disabled : {}) }}
                onClick={nextPage}
                disabled={(meta.offset + (meta.limit || 100)) >= totalRows}
              >
                Next ▶
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default BusAttendanceReports;
